import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { usePeriodStore } from '@/stores/periodStore'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { Plus, Calendar } from 'lucide-react'
import { PeriodCard } from '@/components/period/PeriodCard'
import { CreatePeriodDialog } from '@/components/period/CreatePeriodDialog'

export function PeriodListPage() {
  const navigate = useNavigate()
  const { currentUser } = useAuth()
  const { periods, loading, error, loadPeriods } = usePeriodStore()
  const [showCreateDialog, setShowCreateDialog] = useState(false)

  // Load periods on mount
  useEffect(() => {
    if (!currentUser) return
    loadPeriods(currentUser)
  }, [currentUser, loadPeriods])

  // Handle period card click - navigate to period detail
  const handlePeriodClick = (periodId: string) => {
    navigate(`/periods/${periodId}`)
  }

  // Handle successful period creation - navigate into the new period
  const handlePeriodCreated = (periodId: string) => {
    setShowCreateDialog(false)
    navigate(`/periods/${periodId}`)
  }

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Calendar className="h-12 w-12 mx-auto mb-4 text-muted-foreground animate-pulse" />
          <p className="text-muted-foreground">Laddar löneperioder...</p>
        </div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600 mb-4">{error}</p>
        <Button onClick={() => currentUser && loadPeriods(currentUser)}>
          Försök igen
        </Button>
      </div>
    )
  }

  return (
    <>
      <div className="space-y-6">
        {/* Page Header */}
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <h1 className="text-3xl font-bold tracking-tight">Löneperioder</h1>
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => setShowCreateDialog(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Skapa period
            </Button>
          </div>
        </div>

        <Separator />

        {/* Period Cards Grid */}
        {periods.length === 0 ? (
          <div className="text-center py-12 border rounded-lg">
            <Calendar className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
            <p className="text-lg font-medium mb-2">Inga löneperioder</p>
            <p className="text-muted-foreground mb-4">
              Skapa din första löneperiod för att komma igång med lönehantering.
            </p>
            <Button onClick={() => setShowCreateDialog(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Skapa period
            </Button>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {periods.map((period) => (
              <PeriodCard
                key={period.id}
                period={period}
                onClick={() => handlePeriodClick(period.id)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Create Period Dialog */}
      <CreatePeriodDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        onSuccess={handlePeriodCreated}
      />
    </>
  )
}
